import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRightLeft, CheckCircle2, Clock3, Send, UserRound, XCircle } from 'lucide-react';
import { eventsService } from '../../../../../../services';
import './TransferOwnershipModal.css';

const getDisplayName = (person) => (
    person?.shooterName
    || `${person?.firstName || ''} ${person?.lastName || ''}`.trim()
    || person?.userName
    || person?.toUserName
    || 'Shooter'
);

const dedupeRegisteredUsers = (people, excludedUserIds = []) => {
    const excluded = new Set(excludedUserIds.filter(Boolean).map(Number));
    const seen = new Set();

    return people.filter((person) => {
        const appUserId = Number(person?.appUserId || person?.toUserId || 0);
        if (!appUserId || excluded.has(appUserId) || seen.has(appUserId)) {
            return false;
        }

        seen.add(appUserId);
        return true;
    });
};

const TransferOwnershipModal = ({ isOpen, event, onClose, onTransferChanged }) => {
    const [eligiblePeople, setEligiblePeople] = useState([]);
    const [pendingTransfer, setPendingTransfer] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const selectedPerson = useMemo(
        () => eligiblePeople.find((person) => String(person.appUserId) === String(selectedUserId)) || null,
        [eligiblePeople, selectedUserId]
    );

    useEffect(() => {
        const loadTransferContext = async () => {
            if (!isOpen || !event?.eventId) {
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                const [eventShooters, availableShooters, currentPendingTransfer] = await Promise.all([
                    eventsService.getEventShooters(event.eventId),
                    eventsService.getAvailableShooters(),
                    eventsService.getPendingOwnershipTransfer(event.eventId)
                ]);

                const merged = dedupeRegisteredUsers(
                    [...(Array.isArray(eventShooters) ? eventShooters : []), ...(Array.isArray(availableShooters) ? availableShooters : [])],
                    [event.userId]
                ).sort((left, right) => getDisplayName(left).localeCompare(getDisplayName(right)));

                setEligiblePeople(merged);
                setPendingTransfer(currentPendingTransfer || null);
                setSelectedUserId(currentPendingTransfer?.toUserId ? String(currentPendingTransfer.toUserId) : '');
            } catch (loadError) {
                console.error('Error loading transfer ownership context:', loadError);
                setError(loadError?.message || 'Could not load transfer options right now.');
            } finally {
                setIsLoading(false);
            }
        };

        loadTransferContext();
    }, [event?.eventId, event?.userId, isOpen]);

    const handleRequestTransfer = async () => {
        if (!selectedPerson || !event?.eventId) {
            setError('Choose the registered shooter or friend who should take over this event.');
            return;
        }

        const confirmed = window.confirm(
            `Transfer ownership of "${event.name}" to ${getDisplayName(selectedPerson)}? They will need to accept before the event changes hands.`
        );

        if (!confirmed) {
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const result = await eventsService.requestOwnershipTransfer(event.eventId, {
                targetUserId: Number(selectedPerson.appUserId)
            });

            setPendingTransfer(result);
            if (onTransferChanged) {
                onTransferChanged(result);
            }
        } catch (requestError) {
            console.error('Error requesting ownership transfer:', requestError);
            setError(requestError?.message || 'Could not request the transfer.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancelTransfer = async () => {
        if (!pendingTransfer?.eventOwnershipTransferId) {
            return;
        }

        const confirmed = window.confirm('Cancel this pending ownership transfer request?');
        if (!confirmed) {
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            await eventsService.cancelOwnershipTransfer(pendingTransfer.eventOwnershipTransferId);
            setPendingTransfer(null);
            if (onTransferChanged) {
                onTransferChanged(null);
            }
        } catch (cancelError) {
            console.error('Error cancelling ownership transfer:', cancelError);
            setError(cancelError?.message || 'Could not cancel the transfer request.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="create-event-box transfer-ownership-modal" onClick={(eventClick) => eventClick.stopPropagation()}>
                <div className="modal-header">
                    <div>
                        <h2>Transfer Event Ownership</h2>
                        <p className="create-event-subtitle">
                            Hand the event over safely. The new owner must accept before control moves across.
                        </p>
                    </div>
                </div>

                {error ? <div className="error-message">{error}</div> : null}

                {isLoading ? (
                    <div className="transfer-ownership-loading">
                        <div className="score-events-spinner"></div>
                        <p>Loading transfer options...</p>
                    </div>
                ) : (
                    <>
                        <div className="transfer-ownership-summary">
                            <div className="transfer-ownership-summary-icon">
                                <ArrowRightLeft size={20} />
                            </div>
                            <div>
                                <strong>{event?.name}</strong>
                                <p>
                                    Choose a registered shooter already on the event, or one of your accepted friends. We’ll keep an audit trail of the handover.
                                </p>
                            </div>
                        </div>

                        {pendingTransfer ? (
                            <div className="transfer-ownership-pending-card">
                                <div className="transfer-ownership-pending-head">
                                    <div className="transfer-ownership-pill pending">
                                        <Clock3 size={14} />
                                        Pending acceptance
                                    </div>
                                    <strong>{pendingTransfer.toUserName}</strong>
                                </div>
                                <p>
                                    This transfer was requested on {new Date(pendingTransfer.requestedAt).toLocaleString('en-GB')}. The event will stay with you until it is accepted.
                                </p>
                                <button
                                    type="button"
                                    className="dashboard-card-outline-btn"
                                    onClick={handleCancelTransfer}
                                    disabled={isSubmitting}
                                >
                                    <XCircle size={16} />
                                    Cancel request
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="form-group">
                                    <label className="dashboard-form-label">New owner</label>
                                    <div className="dashboard-form-field">
                                        <select
                                            value={selectedUserId}
                                            onChange={(selectEvent) => setSelectedUserId(selectEvent.target.value)}
                                        >
                                            <option value="">Choose a shooter or friend</option>
                                            {eligiblePeople.map((person) => (
                                                <option key={person.appUserId} value={person.appUserId}>
                                                    {getDisplayName(person)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {selectedPerson ? (
                                    <div className="transfer-ownership-preview">
                                        <UserRound size={18} />
                                        <div>
                                            <strong>{getDisplayName(selectedPerson)}</strong>
                                            <p>
                                                They’ll get a transfer request in their Score Events dashboard and must accept before the event changes owner.
                                            </p>
                                        </div>
                                    </div>
                                ) : null}

                                {eligiblePeople.length === 0 ? (
                                    <div className="transfer-ownership-empty">
                                        <p>No eligible registered shooters or accepted friends are available yet. Add them to the event or your friends list first.</p>
                                    </div>
                                ) : null}

                                <div className="modal-ctas mt-4">
                                    <button
                                        type="button"
                                        className="dashboard-default-btn"
                                        onClick={handleRequestTransfer}
                                        disabled={isSubmitting || !selectedUserId}
                                    >
                                        <Send size={16} />
                                        {isSubmitting ? 'Sending...' : 'Send Transfer Request'}
                                    </button>
                                </div>
                            </>
                        )}

                        <div className="transfer-ownership-notes">
                            <div className="transfer-ownership-note">
                                <CheckCircle2 size={16} />
                                <span>The new owner must explicitly accept.</span>
                            </div>
                            <div className="transfer-ownership-note">
                                <CheckCircle2 size={16} />
                                <span>Both the old and new owner stay linked to the event as registered shooters if needed.</span>
                            </div>
                        </div>
                    </>
                )}

                <div className="modal-ctas mt-4">
                    <button type="button" className="dashboard-card-outline-btn" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TransferOwnershipModal;
